import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessagesSquare, Send, UserPlus, CheckCircle2, Globe, Mail, Phone, MapPin, Loader2, RefreshCw, Bell, BellRing,
} from 'lucide-react';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'converted', label: 'Converted' },
];

const CANNED_REPLIES = [
  { label: 'Opening hours', text: "Thanks for your message! Our office is open Monday to Friday, 9am–5pm. We'll get back to you as soon as we can. You can also call us on 01908 348250." },
  { label: 'We cover your area', text: "Good news — we cover your area! One of our team would be happy to help. Could you confirm your full address and the best number to reach you on?" },
  { label: 'Request a survey', text: "We'd love to arrange a free, no-obligation survey for you. What's your full address and postcode, and when would be a good time for us to call to book it in?" },
  { label: 'Ask for details', text: "Happy to help! To point you in the right direction, could you let me know a little more about what you're looking for (e.g. solar, battery storage, or an EV charger)?" },
];

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB');
};

export default function WebsiteChat() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState({ session: null, messages: [] });
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [cannedReplies, setCannedReplies] = useState(CANNED_REPLIES);
  const threadEndRef = useRef(null);
  const replyRef = useRef(null);

  const enableAlerts = async () => {
    if (typeof Notification === 'undefined') { toast.error('This browser does not support desktop notifications'); return; }
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
      if (p === 'granted') {
        toast.success('Desktop alerts enabled');
        new Notification('Helios CRM', { body: "You'll be alerted here when a new website chat arrives.", icon: '/favicon.ico' });
      } else if (p === 'denied') {
        toast.error('Alerts blocked. Enable notifications for this site in your browser settings.');
      }
    } catch {
      toast.error('Could not enable alerts');
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get('/website-chat/sessions', { params: { filter } });
      setSessions(data.sessions || []);
    } catch { /* noop */ }
  }, [filter]);

  const loadThread = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/website-chat/sessions/${sid}/messages`);
      setThread(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    // Load configurable canned replies (falls back to defaults on error).
    api.get('/website-chat/config')
      .then(({ data }) => { if (data?.canned_replies?.length) setCannedReplies(data.canned_replies); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    // Poll the open conversation quickly (2.5s) so replies land almost instantly,
    // and the session list a little less often (5s).
    const thread = setInterval(() => { if (activeId) loadThread(activeId); }, 2500);
    const list = setInterval(() => { loadSessions(); }, 5000);
    return () => { clearInterval(thread); clearInterval(list); };
  }, [loadSessions, loadThread, activeId]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread.messages]);

  const openSession = async (sid) => {
    setActiveId(sid);
    await loadThread(sid);
    try {
      await api.post(`/website-chat/sessions/${sid}/read`);
      loadSessions();
      window.dispatchEvent(new Event('website-chat-refresh'));
    } catch { /* noop */ }
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      await api.post(`/website-chat/sessions/${activeId}/reply`, { body });
      setReply('');
      await loadThread(activeId);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const convertLead = async () => {
    if (!activeId) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/website-chat/sessions/${activeId}/convert-lead`);
      toast.success(data.already ? 'Already linked to a lead' : 'Lead created from this chat');
      await loadThread(activeId);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally {
      setConverting(false);
    }
  };

  const active = thread.session;

  return (
    <Layout>
      <div className="p-6" data-testid="website-chat-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessagesSquare className="w-6 h-6 text-blue-600" /> Website Chat Messages
            </h1>
            <p className="text-sm text-slate-500 mt-1">Live conversations from the main website chat widget.</p>
          </div>
          <div className="flex items-center gap-2">
            {notifPerm === 'granted' ? (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium px-2" data-testid="chat-alerts-on">
                <BellRing className="w-4 h-4" /> Alerts on
              </span>
            ) : (
              <Button variant="outline" size="sm" onClick={enableAlerts} data-testid="chat-enable-alerts-btn">
                <Bell className="w-4 h-4 mr-2" /> Enable desktop alerts
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={loadSessions} data-testid="chat-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === f.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`chat-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ height: 'calc(100vh - 220px)' }}>
          {/* Session list */}
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl overflow-y-auto" data-testid="chat-session-list">
            {sessions.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">No conversations yet.</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${activeId === s.session_id ? 'bg-blue-50' : ''}`}
                data-testid={`chat-session-${s.session_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800 truncate">{s.name || 'Website Visitor'}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {s.converted && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    {s.unread_count > 0 && (
                      <span className="bg-rose-500 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid={`chat-unread-${s.session_id}`}>
                        {s.unread_count}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{s.last_message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{timeAgo(s.last_message_at)}</p>
              </button>
            ))}
          </div>

          {/* Thread */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                Select a conversation to view messages
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{active.name || 'Website Visitor'}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                      {active.email && <span className="flex items-center gap-1" data-testid="chat-info-email"><Mail className="w-3 h-3" />{active.email}</span>}
                      {active.phone && <span className="flex items-center gap-1" data-testid="chat-info-phone"><Phone className="w-3 h-3" />{active.phone}</span>}
                      {active.postcode && <span className="flex items-center gap-1" data-testid="chat-info-postcode"><MapPin className="w-3 h-3" />{active.postcode}</span>}
                      {active.page_url && <span className="flex items-center gap-1" data-testid="chat-info-page"><Globe className="w-3 h-3" />{active.page_url}</span>}
                    </div>
                  </div>
                  {active.converted ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 flex-shrink-0">Lead created</Badge>
                  ) : (
                    <Button size="sm" onClick={convertLead} disabled={converting} className="flex-shrink-0" data-testid="chat-convert-lead-btn">
                      {converting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                      Convert to lead
                    </Button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50" data-testid="chat-thread">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.direction === 'outbound' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-blue-100' : 'text-slate-400'}`}>
                          {m.direction === 'outbound' ? (m.agent_name || 'Agent') : (active.name || 'Visitor')} · {timeAgo(m.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={threadEndRef} />
                </div>

                <div className="border-t border-slate-100">
                  <div className="flex flex-wrap gap-1.5 px-3 pt-2.5" data-testid="canned-replies">
                    {cannedReplies.map((c) => (
                      <button
                        key={c.label}
                        type="button"
                        onClick={() => { setReply(c.text); replyRef.current?.focus(); }}
                        className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                        data-testid={`canned-${(c.label || '').replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {c.label || 'Reply'}
                      </button>
                    ))}
                  </div>
                  <div className="p-3 flex items-end gap-2">
                    <Textarea
                      ref={replyRef}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Type a reply to the visitor…"
                      rows={2}
                      className="resize-none"
                      data-testid="chat-reply-input"
                    />
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} className="h-10" data-testid="chat-send-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
